import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from 'react';

// ── Color constants ─────────────────────────────────────────────────────────
const INK = '#0e0e0e';
const PAPER = '#f4f1ea';
const MUTED = '#9a948a';

// ── Easing functions ────────────────────────────────────────────────────────
const Easing = {
  linear: (t: number) => t,
  easeInQuad: (t: number) => t * t,
  easeOutQuad: (t: number) => t * (2 - t),
  easeInCubic: (t: number) => t * t * t,
  easeOutCubic: (t: number) => (--t) * t * t + 1,
  easeInOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
  easeOutBack: (t: number) => {
    const c1 = 1.70158, c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
};

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

// ── Timeline context ────────────────────────────────────────────────────────
interface TimelineContextValue {
  time: number;
  duration: number;
  playing: boolean;
}

const TimelineContext = createContext<TimelineContextValue>({ time: 0, duration: 22, playing: true });
const useTime = () => useContext(TimelineContext).time;
const useTimeline = () => useContext(TimelineContext);

// ── Sprite context ──────────────────────────────────────────────────────────
interface SpriteContextValue {
  localTime: number;
  progress: number;
  duration: number;
  visible?: boolean;
}

const SpriteContext = createContext<SpriteContextValue>({ localTime: 0, progress: 0, duration: 0 });
const useSprite = () => useContext(SpriteContext);

function Sprite({ start = 0, end = Infinity, children }: {
  start?: number;
  end?: number;
  children: React.ReactNode;
}) {
  const { time } = useTimeline();
  const visible = time >= start && time <= end;
  if (!visible) return null;

  const duration = end - start;
  const localTime = Math.max(0, time - start);
  const progress = duration > 0 && isFinite(duration) ? clamp(localTime / duration, 0, 1) : 0;
  const value = { localTime, progress, duration, visible };

  return (
    <SpriteContext.Provider value={value}>
      {children}
    </SpriteContext.Provider>
  );
}

// ── SVG drawing primitives ──────────────────────────────────────────────────
function DrawnLine({ x1, y1, x2, y2, progress = 1, strokeWidth = 2, color = INK, dash, opacity = 1 }: {
  x1: number; y1: number; x2: number; y2: number;
  progress?: number; strokeWidth?: number; color?: string; dash?: string; opacity?: number;
}) {
  const len = Math.hypot(x2 - x1, y2 - y1);
  return (
    <line x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={color} strokeWidth={strokeWidth} strokeLinecap="square" opacity={opacity}
      strokeDasharray={dash || `${len}`}
      strokeDashoffset={dash ? 0 : len * (1 - clamp(progress, 0, 1))}
    />
  );
}

function DrawnCircle({ cx, cy, r, progress = 1, strokeWidth = 2, color = INK, fill = 'none' }: {
  cx: number; cy: number; r: number;
  progress?: number; strokeWidth?: number; color?: string; fill?: string;
}) {
  const c = 2 * Math.PI * r;
  return (
    <circle cx={cx} cy={cy} r={r} fill={fill} stroke={color} strokeWidth={strokeWidth}
      strokeDasharray={c} strokeDashoffset={c * (1 - clamp(progress, 0, 1))}
    />
  );
}

// ── Frame chrome ────────────────────────────────────────────────────────────
function StageChrome({ caption, chapter, total }: { caption: string; chapter: number; total: number }) {
  const t = useTime();
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10, fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', color: INK }}>
      <div style={{ position: 'absolute', inset: 48, border: `1px solid ${INK}` }} />
      <div style={{ position: 'absolute', top: 64, left: 72, fontSize: 13, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 500 }}>
        Atelier — A Garment in Four Acts
      </div>
      <div style={{ position: 'absolute', top: 64, right: 72, fontSize: 13, letterSpacing: '0.22em', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
        {String(chapter).padStart(2, '0')} / {String(total).padStart(2, '0')}
      </div>
      <div style={{ position: 'absolute', bottom: 72, left: 72, right: 72, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-0.005em' }}>{caption}</div>
        <div style={{ fontSize: 12, letterSpacing: '0.18em', fontWeight: 400, color: MUTED, textTransform: 'uppercase', fontVariantNumeric: 'tabular-nums' }}>
          T+{t.toFixed(2)}s
        </div>
      </div>
      <svg style={{ position: 'absolute', top: 48, bottom: 48, right: 24, width: 16 }} viewBox="0 0 16 1000" preserveAspectRatio="none">
        {Array.from({ length: 40 }).map((_, i) => (
          <rect key={i} x="4" y={i * 25 + 4} width="8" height="14" fill={INK} opacity={i % 3 === 0 ? 0.9 : 0.2} />
        ))}
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SCENE 1 — The Designer Sketches
// ═══════════════════════════════════════════════════════════════════════════
function SceneDesigner() {
  const { localTime, duration } = useSprite();
  const entry = Easing.easeOutCubic(clamp(localTime / 0.6, 0, 1));
  const exit = Easing.easeInCubic(clamp((localTime - (duration - 0.5)) / 0.5, 0, 1));
  const op = entry - exit;
  const sketchT = clamp((localTime - 0.9) / 2.6, 0, 1);

  const paperX = 620, paperY = 260, paperW = 680, paperH = 660;
  const fx = paperX + paperW * 0.55;
  const headY = paperY + 110;
  const waistY = paperY + 340;
  const hemY = paperY + 600;

  const strokes = [
    { t0: 0.00, t1: 0.10, type: 'circle' as const, cx: fx, cy: headY, r: 26 },
    { t0: 0.10, t1: 0.18, type: 'line' as const, x1: fx, y1: headY + 26, x2: fx, y2: headY + 70 },
    { t0: 0.18, t1: 0.30, type: 'line' as const, x1: fx - 90, y1: headY + 90, x2: fx, y2: headY + 70 },
    { t0: 0.30, t1: 0.42, type: 'line' as const, x1: fx, y1: headY + 70, x2: fx + 90, y2: headY + 90 },
    { t0: 0.42, t1: 0.55, type: 'line' as const, x1: fx - 90, y1: headY + 90, x2: fx - 60, y2: waistY },
    { t0: 0.55, t1: 0.68, type: 'line' as const, x1: fx + 90, y1: headY + 90, x2: fx + 60, y2: waistY },
    { t0: 0.68, t1: 0.78, type: 'line' as const, x1: fx - 60, y1: waistY, x2: fx + 60, y2: waistY },
    { t0: 0.78, t1: 0.90, type: 'line' as const, x1: fx - 60, y1: waistY, x2: fx - 180, y2: hemY },
    { t0: 0.90, t1: 1.00, type: 'line' as const, x1: fx + 60, y1: waistY, x2: fx + 180, y2: hemY },
  ];

  let pencilTipX = fx + 260, pencilTipY = headY - 60;
  if (sketchT > 0 && sketchT < 1) {
    for (const s of strokes) {
      if (sketchT >= s.t0 && sketchT < s.t1) {
        const local = (sketchT - s.t0) / (s.t1 - s.t0);
        if (s.type === 'circle') {
          const a = -Math.PI / 2 + local * Math.PI * 2;
          pencilTipX = s.cx! + s.r! * Math.cos(a);
          pencilTipY = s.cy! + s.r! * Math.sin(a);
        } else {
          pencilTipX = s.x1! + (s.x2! - s.x1!) * local;
          pencilTipY = s.y1! + (s.y2! - s.y1!) * local;
        }
        break;
      }
    }
  } else if (sketchT >= 1) {
    pencilTipX = fx + 180; pencilTipY = hemY;
  }

  const renderStroke = (s: typeof strokes[number], idx: number) => {
    if (sketchT < s.t0) return null;
    const p = clamp((sketchT - s.t0) / (s.t1 - s.t0), 0, 1);
    if (s.type === 'circle') {
      return <DrawnCircle key={idx} cx={s.cx!} cy={s.cy!} r={s.r!} progress={p} strokeWidth={2.5} />;
    }
    return <DrawnLine key={idx} x1={s.x1!} y1={s.y1!} x2={s.x2!} y2={s.y2!} progress={p} strokeWidth={2.5} />;
  };

  return (
    <svg viewBox="0 0 1920 1080" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
      <g opacity={op}>
        <line x1={120} y1={1000} x2={1800} y2={1000} stroke={INK} strokeWidth={1} />
        <line x1={paperX + 30} y1={paperY + paperH} x2={paperX - 10} y2={1000} stroke={INK} strokeWidth={2} />
        <line x1={paperX + paperW - 30} y1={paperY + paperH} x2={paperX + paperW + 10} y2={1000} stroke={INK} strokeWidth={2} />
        <line x1={paperX - 6} y1={995} x2={paperX + paperW + 6} y2={995} stroke={INK} strokeWidth={2} />
        <rect x={paperX} y={paperY} width={paperW} height={paperH} fill="#ffffff" stroke={INK} strokeWidth={2} />
        <g opacity={0.7}>
          <rect x={paperX - 14} y={paperY - 14} width={42} height={28} fill={MUTED} opacity={0.35} transform={`rotate(-12 ${paperX + 7} ${paperY})`} />
          <rect x={paperX + paperW - 28} y={paperY - 14} width={42} height={28} fill={MUTED} opacity={0.35} transform={`rotate(10 ${paperX + paperW - 7} ${paperY})`} />
        </g>
        {Array.from({ length: 7 }).map((_, i) => (
          <line key={`gv${i}`} x1={paperX + (paperW / 8) * (i + 1)} y1={paperY + 12} x2={paperX + (paperW / 8) * (i + 1)} y2={paperY + paperH - 12} stroke={INK} strokeWidth={0.5} opacity={0.08} />
        ))}
        {Array.from({ length: 7 }).map((_, i) => (
          <line key={`gh${i}`} x1={paperX + 12} y1={paperY + (paperH / 8) * (i + 1)} x2={paperX + paperW - 12} y2={paperY + (paperH / 8) * (i + 1)} stroke={INK} strokeWidth={0.5} opacity={0.08} />
        ))}
        <text x={paperX + 30} y={paperY + 50} fontSize={14} fill={INK} letterSpacing="0.3em" opacity={0.55} fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif'>
          CROQUIS · S/S
        </text>
        <line x1={paperX + 30} y1={paperY + 64} x2={paperX + 220} y2={paperY + 64} stroke={INK} strokeWidth={0.8} opacity={0.4} />
        <g>{strokes.map(renderStroke)}</g>
        {[0, 1, 2].map((i) => {
          const appear = clamp((localTime - (0.1 + i * 0.16)) / 0.4, 0, 1);
          const angles = [-8, 4, -3];
          return (
            <g key={i} opacity={appear} transform={`translate(${180 + i * 100}, ${170 + (1 - appear) * 14}) rotate(${angles[i]} 50 60)`}>
              <rect x={0} y={0} width={100} height={130} fill="#ffffff" stroke={INK} strokeWidth={1.5} />
              <rect x={8} y={8} width={84} height={92} fill={INK} opacity={0.08} />
              <rect x={8} y={8} width={84} height={92} fill="none" stroke={INK} strokeWidth={0.6} opacity={0.5} />
              <line x1={8} y1={112} x2={76} y2={112} stroke={INK} strokeWidth={1} />
              <line x1={8} y1={120} x2={50} y2={120} stroke={INK} strokeWidth={1} />
              <circle cx={50} cy={-2} r={3} fill={INK} />
            </g>
          );
        })}
        <g transform={`translate(${pencilTipX}, ${pencilTipY})`}>
          <g transform="rotate(-32)">
            <rect x={6} y={-5} width={170} height={10} fill="#fff" stroke={INK} strokeWidth={2} />
            <rect x={176} y={-7} width={14} height={14} fill={INK} />
            <rect x={190} y={-6} width={18} height={12} fill={MUTED} stroke={INK} strokeWidth={1.5} />
            <line x1={40} y1={-5} x2={40} y2={5} stroke={INK} strokeWidth={0.8} />
            <line x1={70} y1={-5} x2={70} y2={5} stroke={INK} strokeWidth={0.8} />
            <line x1={100} y1={-5} x2={100} y2={5} stroke={INK} strokeWidth={0.8} />
            <line x1={130} y1={-5} x2={130} y2={5} stroke={INK} strokeWidth={0.8} />
            <polygon points="6,-5 6,5 -8,0" fill={INK} />
          </g>
          <g transform="rotate(-32) translate(6, 0)">
            <path d="M 28 -22 Q 14 -28 6 -22 Q 0 -16 0 -8 L 0 8 Q 0 16 6 22 Q 14 28 28 22 L 50 16 Q 56 8 56 0 Q 56 -8 50 -16 Z" fill="#fff" stroke={INK} strokeWidth={2} />
            <line x1={20} y1={-10} x2={42} y2={-6} stroke={INK} strokeWidth={1} opacity={0.4} />
            <line x1={20} y1={10} x2={42} y2={6} stroke={INK} strokeWidth={1} opacity={0.4} />
            <rect x={48} y={-22} width={60} height={44} fill="#fff" stroke={INK} strokeWidth={2} />
            <line x1={96} y1={-22} x2={96} y2={22} stroke={INK} strokeWidth={2} />
            <circle cx={102} cy={0} r={2.5} fill={INK} />
          </g>
        </g>
        {sketchT > 0.95 && (
          <g opacity={clamp((sketchT - 0.95) * 20, 0, 1)} transform={`translate(${paperX + 36}, ${paperY + paperH - 60})`}>
            <text fontSize={11} fill={INK} letterSpacing="0.22em" fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' opacity={0.7}>SIGNED</text>
            <path d="M 0 28 C 14 14, 28 42, 50 22 S 78 8, 96 26" fill="none" stroke={INK} strokeWidth={2} />
          </g>
        )}
      </g>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SCENE 2 — Fabric: rolled out, measured, cut
// ═══════════════════════════════════════════════════════════════════════════
function SceneFabric() {
  const { localTime, duration } = useSprite();
  const entry = Easing.easeOutCubic(clamp(localTime / 0.4, 0, 1));
  const exit = Easing.easeInCubic(clamp((localTime - (duration - 0.5)) / 0.5, 0, 1));
  const op = entry - exit;

  const TABLE_BOTTOM = 820, TABLE_LEFT = 120, TABLE_RIGHT = 1800;
  const FAB_Y = 430, FAB_H = 320, FAB_LEFT = 240, FAB_RIGHT_MAX = 1620;
  const fabMaxLen = FAB_RIGHT_MAX - FAB_LEFT;

  const rollProgress = Easing.easeInOutCubic(clamp(localTime / 1.2, 0, 1));
  const boltX = -160 + rollProgress * (FAB_LEFT + 60);
  const unfurl = clamp((localTime - 0.3) / 2.2, 0, 1);
  const fabLen = unfurl * fabMaxLen;
  const rot = rollProgress < 1 ? rollProgress * 900 : 900;
  const chalk = clamp((localTime - 2.5) / 1.0, 0, 1);
  const cutT = clamp((localTime - 3.0) / 0.8, 0, 1);
  const scissorX = FAB_LEFT + 80 + cutT * (fabMaxLen - 160);
  const scissorY = FAB_Y + FAB_H / 2;

  return (
    <svg viewBox="0 0 1920 1080" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
      <g opacity={op}>
        <line x1={TABLE_LEFT} y1={TABLE_BOTTOM} x2={TABLE_RIGHT} y2={TABLE_BOTTOM} stroke={INK} strokeWidth={1.5} />
        <line x1={TABLE_LEFT + 20} y1={TABLE_BOTTOM} x2={TABLE_LEFT + 20} y2={1000} stroke={INK} strokeWidth={1.5} />
        <line x1={TABLE_RIGHT - 20} y1={TABLE_BOTTOM} x2={TABLE_RIGHT - 20} y2={1000} stroke={INK} strokeWidth={1.5} />
        <line x1={FAB_LEFT} y1={FAB_Y - 36} x2={FAB_RIGHT_MAX} y2={FAB_Y - 36} stroke={INK} strokeWidth={1} />
        {Array.from({ length: 29 }).map((_, i) => {
          const x = FAB_LEFT + i * (fabMaxLen / 28);
          const tall = i % 5 === 0;
          return (
            <g key={i}>
              <line x1={x} y1={FAB_Y - 36} x2={x} y2={FAB_Y - 36 + (tall ? 12 : 6)} stroke={INK} strokeWidth={tall ? 1 : 0.6} />
              {tall && i > 0 && (
                <text x={x} y={FAB_Y - 44} fontSize={11} fill={INK} textAnchor="middle" fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' letterSpacing="0.08em" opacity={0.55}>
                  {i * 5}
                </text>
              )}
            </g>
          );
        })}
        <g>
          <rect x={FAB_LEFT} y={FAB_Y} width={fabLen} height={FAB_H} fill="#fbf8f0" stroke={INK} strokeWidth={2} />
          {Array.from({ length: 24 }).map((_, i) => (
            <line key={`hh${i}`} x1={FAB_LEFT + 4} y1={FAB_Y + 12 + i * 11} x2={FAB_LEFT + Math.max(0, fabLen - 4)} y2={FAB_Y + 12 + i * 11} stroke={INK} strokeWidth={0.5} opacity={i % 3 === 0 ? 0.28 : 0.1} />
          ))}
          {fabLen > 80 && Array.from({ length: Math.floor(fabLen / 32) }).map((_, i) => (
            <line key={`dh${i}`} x1={FAB_LEFT + i * 32} y1={FAB_Y} x2={FAB_LEFT + 32 + i * 32} y2={FAB_Y + FAB_H} stroke={INK} strokeWidth={0.4} opacity={0.14} />
          ))}
          {fabLen < fabMaxLen && (
            <g>
              {Array.from({ length: 6 }).map((_, i) => (
                <line key={i} x1={FAB_LEFT + fabLen} y1={FAB_Y + 10 + i * 50} x2={FAB_LEFT + fabLen + 14} y2={FAB_Y + 5 + i * 50} stroke={INK} strokeWidth={1} opacity={0.5} />
              ))}
            </g>
          )}
        </g>
        <g opacity={chalk}>
          <g transform={`translate(${FAB_LEFT + 80}, ${FAB_Y + 30})`}>
            <polyline points="0,0 140,0 160,60 150,180 20,200 -10,80" fill="none" stroke={INK} strokeWidth={1.5} strokeDasharray="8 5" opacity={chalk * 0.85} />
            <line x1={80} y1={30} x2={80} y2={170} stroke={INK} strokeWidth={1} opacity={chalk * 0.5} />
            <polygon points="76,30 84,30 80,22" fill={INK} opacity={chalk * 0.5} />
            <polygon points="76,170 84,170 80,178" fill={INK} opacity={chalk * 0.5} />
            <text x={86} y={104} fontSize={9} fill={INK} fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' letterSpacing="0.15em" opacity={chalk * 0.6}>GRAIN</text>
          </g>
          <g transform={`translate(${FAB_LEFT + 420}, ${FAB_Y + 40})`}>
            <path d="M 0 30 Q 30 -10 70 0 Q 110 -10 140 30 L 130 180 L 10 180 Z" fill="none" stroke={INK} strokeWidth={1.5} strokeDasharray="8 5" opacity={chalk * 0.85} />
            <text x={70} y={108} fontSize={10} fill={INK} fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' textAnchor="middle" letterSpacing="0.3em" opacity={chalk * 0.6}>SLEEVE</text>
          </g>
          <g transform={`translate(${FAB_LEFT + 720}, ${FAB_Y + 30})`}>
            <polyline points="0,0 180,0 220,200 -40,200 0,0" fill="none" stroke={INK} strokeWidth={1.5} strokeDasharray="8 5" opacity={chalk * 0.85} />
            <text x={90} y={108} fontSize={10} fill={INK} fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' textAnchor="middle" letterSpacing="0.3em" opacity={chalk * 0.6}>SKIRT</text>
          </g>
        </g>
        {cutT > 0 && (
          <line x1={FAB_LEFT + 80} y1={scissorY} x2={scissorX - 10} y2={scissorY} stroke={INK} strokeWidth={1.2} strokeDasharray="4 4" opacity={0.7} />
        )}
        {cutT > 0 && cutT < 1 && (
          <g transform={`translate(${scissorX}, ${scissorY}) rotate(${-15 + Math.sin(localTime * 18) * 8})`}>
            <polygon points="-2,0 -2,-60 4,-80 8,-72 4,0" fill={PAPER} stroke={INK} strokeWidth={2} />
            <polygon points="2,0 2,-60 -4,-80 -8,-72 -4,0" fill={PAPER} stroke={INK} strokeWidth={2} />
            <circle cx={0} cy={0} r={4} fill={INK} />
            <path d="M -2 4 Q -28 12 -28 28 Q -28 44 -12 44 Q 4 44 4 28 Q 4 16 -2 12 Z" fill="none" stroke={INK} strokeWidth={2.5} />
            <path d="M 2 4 Q 28 12 28 28 Q 28 44 12 44 Q -4 44 -4 28 Q -4 16 2 12 Z" fill="none" stroke={INK} strokeWidth={2.5} />
          </g>
        )}
        <g transform={`translate(${boltX - 80}, ${FAB_Y - 30}) rotate(${rot}, 80, 170)`}>
          <circle cx={80} cy={170} r={75} fill={PAPER} stroke={INK} strokeWidth={3} />
          <circle cx={80} cy={170} r={58} fill="none" stroke={INK} strokeWidth={1.5} opacity={0.55} />
          <circle cx={80} cy={170} r={42} fill="none" stroke={INK} strokeWidth={1.5} opacity={0.45} />
          <circle cx={80} cy={170} r={26} fill="none" stroke={INK} strokeWidth={1.5} opacity={0.35} />
          <circle cx={80} cy={170} r={10} fill={INK} />
          <line x1={80} y1={95} x2={80} y2={108} stroke={INK} strokeWidth={2.5} />
          <line x1={80} y1={232} x2={80} y2={245} stroke={INK} strokeWidth={2.5} />
          <line x1={5} y1={170} x2={18} y2={170} stroke={INK} strokeWidth={2.5} />
          <line x1={142} y1={170} x2={155} y2={170} stroke={INK} strokeWidth={2.5} />
        </g>
        <g opacity={chalk * 0.85} transform={`translate(${FAB_LEFT + fabMaxLen / 2}, ${FAB_Y - 60})`}>
          <line x1={-90} y1={0} x2={90} y2={0} stroke={INK} strokeWidth={1} />
          <text x={0} y={-14} fontSize={12} fill={INK} textAnchor="middle" fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' letterSpacing="0.4em" fontWeight={500}>
            COTTON POPLIN · 1.4 M
          </text>
        </g>
      </g>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SCENE 3 — Sewing Machine Stitches
// ═══════════════════════════════════════════════════════════════════════════
function SceneSewingMachine() {
  const { localTime, duration } = useSprite();
  const entry = Easing.easeOutCubic(clamp(localTime / 0.6, 0, 1));
  const exit = Easing.easeInCubic(clamp((localTime - (duration - 0.5)) / 0.5, 0, 1));
  const op = entry - exit;

  const NEEDLE_X = 960;
  const BED_Y = 740, FABRIC_Y = 692, FABRIC_H = 48;
  const STITCH_Y = FABRIC_Y + FABRIC_H / 2;

  const stitching = localTime > 0.7;
  const needleBob = stitching ? (Math.sin((localTime - 0.7) * 24) + 1) / 2 : 0;
  const needleDrop = needleBob * 30;
  const wheelRot = stitching ? (localTime - 0.7) * 24 * (180 / Math.PI) : 0;

  const driftRate = 110;
  const fabricDrift = stitching ? Math.min(620, (localTime - 0.7) * driftRate) : 0;
  const stitchHz = 24 / (2 * Math.PI);
  const stitchCount = stitching ? Math.floor((localTime - 0.7) * stitchHz) : 0;
  const stitchSpacing = driftRate / stitchHz;
  const stitchEndX = NEEDLE_X;
  const stitchStartX = NEEDLE_X - stitchCount * stitchSpacing;

  return (
    <svg viewBox="0 0 1920 1080" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
      <g opacity={op}>
        <line x1={100} y1={870} x2={1820} y2={870} stroke={INK} strokeWidth={1.5} />
        <line x1={100} y1={870} x2={140} y2={1020} stroke={INK} strokeWidth={1} opacity={0.5} />
        <line x1={1820} y1={870} x2={1780} y2={1020} stroke={INK} strokeWidth={1} opacity={0.5} />
        <g>
          <rect x={NEEDLE_X - 380} y={BED_Y} width={760} height={36} fill={INK} />
          <rect x={NEEDLE_X - 380} y={BED_Y - 12} width={760} height={14} fill={PAPER} stroke={INK} strokeWidth={2} />
        </g>
        <rect x={NEEDLE_X - fabricDrift} y={FABRIC_Y} width={fabricDrift} height={FABRIC_H} fill="#fbf8f0" stroke={INK} strokeWidth={2} />
        <rect x={NEEDLE_X} y={FABRIC_Y} width={Math.max(0, 720 - fabricDrift)} height={FABRIC_H} fill="#fbf8f0" stroke={INK} strokeWidth={2} />
        {Array.from({ length: 4 }).map((_, i) => (
          <line key={`w${i}`} x1={NEEDLE_X + 10} y1={FABRIC_Y + 8 + i * 11} x2={NEEDLE_X + Math.max(10, 720 - fabricDrift) - 10} y2={FABRIC_Y + 8 + i * 11} stroke={INK} strokeWidth={0.6} opacity={0.3} />
        ))}
        {stitchCount > 0 && (
          <line x1={Math.max(NEEDLE_X - fabricDrift + 4, stitchStartX)} y1={STITCH_Y} x2={stitchEndX - 6} y2={STITCH_Y} stroke={INK} strokeWidth={2} strokeDasharray="10 6" />
        )}
        <g>
          <rect x={NEEDLE_X + 230} y={380} width={150} height={340} fill={PAPER} stroke={INK} strokeWidth={2.5} />
          <g transform={`translate(${NEEDLE_X + 380}, 460) rotate(${wheelRot})`}>
            <circle cx={0} cy={0} r={44} fill={PAPER} stroke={INK} strokeWidth={2.5} />
            <circle cx={0} cy={0} r={8} fill={INK} />
            <line x1={-44} y1={0} x2={44} y2={0} stroke={INK} strokeWidth={1} opacity={0.35} />
            <line x1={0} y1={-44} x2={0} y2={44} stroke={INK} strokeWidth={1} opacity={0.35} />
            <line x1={-31} y1={-31} x2={31} y2={31} stroke={INK} strokeWidth={1} opacity={0.35} />
            <line x1={-31} y1={31} x2={31} y2={-31} stroke={INK} strokeWidth={1} opacity={0.35} />
            <circle cx={28} cy={0} r={3} fill={INK} />
          </g>
          <rect x={NEEDLE_X - 60} y={400} width={290} height={80} fill={PAPER} stroke={INK} strokeWidth={2.5} />
          <rect x={NEEDLE_X + 10} y={420} width={200} height={40} fill={PAPER} stroke={INK} strokeWidth={1.2} />
          <text x={NEEDLE_X + 110} y={447} fontSize={16} fill={INK} fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' textAnchor="middle" dominantBaseline="middle" letterSpacing="0.32em" fontWeight={700}>
            ATELIER
          </text>
          <g transform={`translate(${NEEDLE_X + 70}, 358)`}>
            <rect x={-2} y={0} width={36} height={6} fill={INK} />
            <rect x={-2} y={36} width={36} height={6} fill={INK} />
            <rect x={2} y={6} width={28} height={30} fill={PAPER} stroke={INK} strokeWidth={1.5} />
            {Array.from({ length: 8 }).map((_, i) => (
              <line key={i} x1={2} y1={9 + i * 3.5} x2={30} y2={9 + i * 3.5} stroke={INK} strokeWidth={1} />
            ))}
          </g>
          <circle cx={NEEDLE_X - 40} cy={440} r={14} fill={PAPER} stroke={INK} strokeWidth={2} />
          <circle cx={NEEDLE_X - 40} cy={440} r={3} fill={INK} />
          <path d={`M ${NEEDLE_X + 86} 364 Q ${NEEDLE_X + 40} 420 ${NEEDLE_X - 40} 440 Q ${NEEDLE_X - 30} 480 ${NEEDLE_X - 12} 500 L ${NEEDLE_X} ${640 + needleDrop}`} fill="none" stroke={INK} strokeWidth={1.2} opacity={0.75} />
          <rect x={NEEDLE_X - 30} y={480} width={50} height={180} fill={PAPER} stroke={INK} strokeWidth={2.5} />
          <g transform={`translate(0, ${needleDrop})`}>
            <rect x={NEEDLE_X - 6} y={580} width={14} height={70} fill={INK} />
            <line x1={NEEDLE_X} y1={650} x2={NEEDLE_X} y2={STITCH_Y - 4} stroke={INK} strokeWidth={3} />
            <polygon points={`${NEEDLE_X - 3},${STITCH_Y - 6} ${NEEDLE_X + 3},${STITCH_Y - 6} ${NEEDLE_X},${STITCH_Y - 1}`} fill={INK} />
            <g>
              <rect x={NEEDLE_X - 22} y={STITCH_Y - 4} width={44} height={4} fill={INK} />
              <rect x={NEEDLE_X - 22} y={STITCH_Y - 14} width={6} height={14} fill={INK} />
              <rect x={NEEDLE_X + 16} y={STITCH_Y - 14} width={6} height={14} fill={INK} />
            </g>
          </g>
          {stitching && needleBob > 0.85 && (
            <g opacity={(needleBob - 0.85) * 6}>
              <circle cx={NEEDLE_X - 14} cy={STITCH_Y - 18} r={1.5} fill={INK} />
              <circle cx={NEEDLE_X + 14} cy={STITCH_Y - 18} r={1.5} fill={INK} />
              <circle cx={NEEDLE_X - 20} cy={STITCH_Y - 8} r={1} fill={INK} />
              <circle cx={NEEDLE_X + 20} cy={STITCH_Y - 8} r={1} fill={INK} />
            </g>
          )}
        </g>
        <g transform="translate(1500, 200)">
          <line x1={0} y1={0} x2={260} y2={0} stroke={INK} strokeWidth={1} opacity={0.6} />
          <text x={0} y={28} fontSize={11} fill={INK} fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' letterSpacing="0.32em" fontWeight={500}>
            STITCH COUNT
          </text>
          <text x={0} y={108} fontSize={72} fill={INK} fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' fontWeight={700} letterSpacing="-0.03em" fontVariantNumeric="tabular-nums">
            {String(stitchCount * 7).padStart(4, '0')}
          </text>
          <text x={0} y={138} fontSize={11} fill={INK} fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' letterSpacing="0.32em" opacity={0.55}>
            SPM · {Math.round(stitchHz * 60)}
          </text>
          <line x1={0} y1={154} x2={260} y2={154} stroke={INK} strokeWidth={1} opacity={0.6} />
        </g>
      </g>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SCENE 4 — The Garment Appears
// ═══════════════════════════════════════════════════════════════════════════
function SceneGarment() {
  const { localTime, duration } = useSprite();
  const bodice = Easing.easeOutCubic(clamp(localTime / 1.0, 0, 1));
  const skirt = Easing.easeOutCubic(clamp((localTime - 0.6) / 1.0, 0, 1));
  const sleeves = Easing.easeOutCubic(clamp((localTime - 1.2) / 0.8, 0, 1));
  const detail = clamp((localTime - 2.0) / 1.0, 0, 1);
  const label = clamp((localTime - 2.6) / 0.8, 0, 1);
  const sway = Math.sin(localTime * 1.4) * 1.5;
  const cx = 960;
  const shoulderL = cx - 110, shoulderR = cx + 110;
  const waistL = cx - 80, waistR = cx + 80;
  const hemL = cx - 240, hemR = cx + 240, hemY = 880, waistY = 560;

  const bodicePath = `M ${shoulderL} 380 L ${cx - 20} 430 L ${cx + 20} 430 L ${shoulderR} 380 L ${waistR} ${waistY} L ${waistL} ${waistY} Z`;
  const skirtPath = `M ${waistL} ${waistY} L ${hemL} ${hemY} L ${hemR} ${hemY} L ${waistR} ${waistY} Z`;
  const sleeveLPath = `M ${shoulderL} 380 L ${shoulderL - 70} 460 L ${shoulderL - 30} 490 L ${shoulderL + 10} 430`;
  const sleeveRPath = `M ${shoulderR} 380 L ${shoulderR + 70} 460 L ${shoulderR + 30} 490 L ${shoulderR - 10} 430`;

  return (
    <svg viewBox="0 0 1920 1080" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
      <line x1={300} y1={260} x2={1620} y2={260} stroke={INK} strokeWidth={2} />
      <g transform={`translate(${cx + sway}, 0)`}>
        <path d="M 0 260 L 0 290" stroke={INK} strokeWidth={3} fill="none" />
        <path d="M -8 260 Q -8 240 0 232 Q 12 232 12 250" stroke={INK} strokeWidth={3} fill="none" />
        <path d="M 0 290 L -110 370 L 110 370 Z" stroke={INK} strokeWidth={2.5} fill="none" />
        <line x1={-110} y1={370} x2={110} y2={370} stroke={INK} strokeWidth={2.5} />
      </g>
      <g transform={`translate(${sway}, 0)`}>
        <g opacity={skirt}>
          <path d={skirtPath} fill={INK} stroke={INK} strokeWidth={2.5} opacity={skirt * 0.92} />
          {detail > 0 && Array.from({ length: 7 }).map((_, i) => {
            const xT = waistL + ((waistR - waistL) / 8) * (i + 1);
            const xB = hemL + ((hemR - hemL) / 8) * (i + 1);
            return (
              <line key={i} x1={xT} y1={waistY} x2={xT + (xB - xT) * detail} y2={waistY + (hemY - waistY) * detail} stroke={PAPER} strokeWidth={1} opacity={0.35} />
            );
          })}
        </g>
        <g opacity={bodice}>
          <path d={bodicePath} fill={INK} stroke={INK} strokeWidth={2.5} />
          <path d={`M ${shoulderL} 380 L ${cx - 20} 430 L ${cx + 20} 430 L ${shoulderR} 380`} fill={PAPER} stroke={INK} strokeWidth={2} />
        </g>
        <g opacity={sleeves}>
          <path d={sleeveLPath} fill={INK} stroke={INK} strokeWidth={2.5} />
          <path d={sleeveRPath} fill={INK} stroke={INK} strokeWidth={2.5} />
        </g>
        {detail > 0 && (
          <line x1={waistL} y1={waistY} x2={waistR} y2={waistY} stroke={PAPER} strokeWidth={1.5} opacity={detail * 0.5} />
        )}
        {detail > 0 && (
          <g opacity={detail}>
            <line x1={cx} y1={430} x2={cx} y2={waistY} stroke={PAPER} strokeWidth={1} strokeDasharray="3 4" opacity={0.5} />
          </g>
        )}
      </g>
      <g opacity={label} transform={`translate(${cx - 140}, ${940 + (1 - label) * 20})`}>
        <line x1={140} y1={-40} x2={140} y2={0} stroke={INK} strokeWidth={1} />
        <rect x={0} y={0} width={280} height={64} fill="none" stroke={INK} strokeWidth={1.5} />
        <text x={140} y={26} fontSize={12} fill={INK} fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' textAnchor="middle" letterSpacing="0.4em">
          ATELIER N°04
        </text>
        <text x={140} y={50} fontSize={20} fill={INK} fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif' textAnchor="middle" letterSpacing="0.06em" fontWeight={600}>
          FINISHED
        </text>
      </g>
      {label > 0 && [
        [220, 220], [1700, 220], [220, 980], [1700, 980]
      ].map(([x, y], i) => (
        <g key={i} opacity={label}>
          <line x1={x - 10} y1={y} x2={x + 10} y2={y} stroke={INK} strokeWidth={1} />
          <line x1={x} y1={y - 10} x2={x} y2={y + 10} stroke={INK} strokeWidth={1} />
        </g>
      ))}
    </svg>
  );
}

// ── Chapter transition cards ────────────────────────────────────────────────
function Chapter({ n, label }: { n: number; label: string }) {
  const { localTime, duration } = useSprite();
  const enter = Easing.easeOutCubic(clamp(localTime / 0.5, 0, 1));
  const out = Easing.easeInCubic(clamp((localTime - (duration - 0.4)) / 0.4, 0, 1));
  const op = enter - out;
  return (
    <div style={{
      position: 'absolute', inset: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: INK, opacity: op,
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', color: PAPER,
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 14, letterSpacing: '0.4em', fontWeight: 400, opacity: 0.65, marginBottom: 28 }}>
          ACT {String(n).padStart(2, '0')}
        </div>
        <div style={{ fontSize: 96, fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1 }}>
          {label}
        </div>
        <div style={{ marginTop: 36, display: 'flex', justifyContent: 'center', gap: 6 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{
              width: i === n ? 32 : 8, height: 2,
              background: PAPER, opacity: i === n ? 1 : 0.3,
              transition: 'width 200ms',
            }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ChapterMarker({ start, end, n, label }: { start: number; end: number; n: number; label: string }) {
  return (
    <Sprite start={start} end={end}>
      <Chapter n={n} label={label} />
    </Sprite>
  );
}

// ── Main assembled animation content ────────────────────────────────────────
function AnimationContent() {
  const t = useTime();
  let chapter = 1;
  let caption = 'The designer drafts the first idea.';
  if (t >= 6 && t < 12) { chapter = 2; caption = 'Fabric is measured, marked, and cut.'; }
  else if (t >= 12 && t < 18) { chapter = 3; caption = 'Stitches lock the shape in place.'; }
  else if (t >= 18) { chapter = 4; caption = 'A garment, freshly made.'; }

  return (
    <>
      <StageChrome caption={caption} chapter={chapter} total={4} />
      <Sprite start={1.0} end={6.0}><SceneDesigner /></Sprite>
      <Sprite start={7.0} end={11.0}><SceneFabric /></Sprite>
      <Sprite start={12.0} end={17.0}><SceneSewingMachine /></Sprite>
      <Sprite start={18.0} end={22.0}><SceneGarment /></Sprite>
      <ChapterMarker start={0.0} end={1.0} n={1} label="Sketch" />
      <ChapterMarker start={6.0} end={7.0} n={2} label="Cut" />
      <ChapterMarker start={11.0} end={12.0} n={3} label="Sew" />
      <ChapterMarker start={17.0} end={18.0} n={4} label="Wear" />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Exported component — self-contained auto-playing animation
// ═══════════════════════════════════════════════════════════════════════════
export const AtelierAnimation: React.FC = () => {
  const DURATION = 22;
  const WIDTH = 1920;
  const HEIGHT = 1080;

  const [time, setTime] = useState(0);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Animation loop
  useEffect(() => {
    const step = (ts: number) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;
      setTime((t) => {
        let next = t + dt;
        if (next >= DURATION) next = next % DURATION;
        return next;
      });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTsRef.current = null;
    };
  }, []);

  // Auto-scale to fit container
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const measure = () => {
      const s = el.clientWidth / WIDTH;
      setScale(Math.max(0.05, s));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const ctxValue = useMemo(
    () => ({ time, duration: DURATION, playing: true }),
    [time]
  );

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: 12,
        boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
      }}
    >
      <div style={{
        width: WIDTH,
        height: HEIGHT,
        background: PAPER,
        position: 'relative',
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        overflow: 'hidden',
      }}>
        <TimelineContext.Provider value={ctxValue}>
          <AnimationContent />
        </TimelineContext.Provider>
      </div>
      {/* Spacer to give parent correct height based on scaled content */}
      <div style={{ height: HEIGHT * scale - HEIGHT, marginTop: -HEIGHT + HEIGHT * scale }} />
    </div>
  );
};
